import cn from "classnames";
import Flags from "components/shared/form/flags";
import Loader from "components/shared/loader";
import Select from "components/shared/select";
import { billingNotificationTypes } from "helpers/enums";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    addUserToNotification,
    applyNotificationSetting,
    getNotificationUsers,
    removeUserFromNotification,
} from "sources/billing";
import { adminsList } from "sources/books/employees";

const notificationFlags = [
    { label: "E-mail", value: "MailNotifications", intValue: 2 },
    { label: "СМС", value: "SmsNotifications", intValue: 4 },
];

const defaultNotificationFlags = notificationFlags.reduce((sum, item) => sum + item.intValue, 0);

const fallbackUserName = (userId) => `Пользователь #${userId}`;

const getSetting = (user, type) => {
    return user.settings?.find((setting) => setting.type === type);
};

const updateUserSetting = (users, userId, type, flags) =>
    users.map((user) => {
        if (user.userId !== userId) {
            return user;
        }

        const settings = user.settings || [];
        const hasSetting = settings.some((setting) => setting.type === type);
        const nextSettings = hasSetting
            ? settings.map((setting) => (setting.type === type ? { ...setting, flags } : setting))
            : [...settings, { type, flags }];

        return { ...user, settings: nextSettings };
    });

export default function BillingNotificationUsers(props) {
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [savingKey, setSavingKey] = useState("");

    const currentUserId = Number(props.profile?.id);
    const selectedUserIds = useMemo(() => users.map((user) => user.userId), [users]);
    const hasSelectedUser = selectedUserId !== null && selectedUserId !== undefined;

    const getItems = useCallback(() => {
        setIsLoading(true);
        return getNotificationUsers()
            .then((items) => setUsers(items || []))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        getItems();
    }, [getItems]);

    const addUser = () => {
        if (!hasSelectedUser || savingKey) {
            return;
        }

        setSavingKey("add");
        addUserToNotification({ userId: Number(selectedUserId) })
            .then((res) => {
                if (res) {
                    setSelectedUserId(null);
                    return getItems();
                }

                return undefined;
            })
            .finally(() => setSavingKey(""));
    };

    const removeUser = (user) => {
        props.modal({
            name: "confirm",
            title: "Удаление получателя",
            text: `Удалить ${user.userName || fallbackUserName(user.userId)} из получателей уведомлений об оплате?`,
            buttons: { confirm: "Удалить", cancel: "Отмена" },
            action: () => {
                setSavingKey(`remove-${user.userId}`);
                return removeUserFromNotification({ userId: user.userId })
                    .then((res) => {
                        if (res) {
                            return getItems();
                        }

                        return undefined;
                    })
                    .finally(() => setSavingKey(""));
            },
        });
    };

    const applySetting = (user, type, flags) => {
        setSavingKey(`${user.userId}-${type}`);
        applyNotificationSetting({ type, flags })
            .then((res) => {
                if (res) {
                    setUsers((prevUsers) => updateUserSetting(prevUsers, user.userId, type, flags));
                }
            })
            .finally(() => setSavingKey(""));
    };

    const renderFlags = (user, type) => {
        const setting = getSetting(user, type);
        const flags = Number(setting?.flags ?? defaultNotificationFlags);
        const canEdit = user.userId === currentUserId;
        const disabled = !canEdit || !!savingKey;

        return (
            <td
                key={type}
                title={canEdit ? undefined : "Настройка доступна только текущему пользователю"}
            >
                <Flags
                    inputProps={{ name: `billingNotification_${user.userId}_${type}` }}
                    flags={notificationFlags}
                    value={flags}
                    disabled={disabled}
                    onChange={({ value }) => applySetting(user, type, value)}
                    fieldWrapper="p-0"
                    bWrapper="mb-0"
                />
            </td>
        );
    };

    const renderUser = (user) => (
        <tr key={user.userId}>
            <td className="text-nowrap">{user.userName || fallbackUserName(user.userId)}</td>
            {billingNotificationTypes.map((type) => renderFlags(user, type.value))}
            <td className="text-right">
                {props.editable && (
                    <button
                        type="button"
                        className="btn btn-xs btn-outline-danger"
                        title="Удалить получателя"
                        aria-label="Удалить получателя"
                        disabled={!!savingKey}
                        onClick={() => removeUser(user)}
                    >
                        <i className="icon-uEA31-trash" />
                    </button>
                )}
            </td>
        </tr>
    );

    if (isLoading) {
        return <Loader prefix="margin-center" />;
    }

    return (
        <>
            {props.editable && (
                <div className="d-flex align-items-start mb-3">
                    <div className="flex-grow-1">
                        <Select
                            inputProps={{ name: "billingNotificationUserId" }}
                            url={adminsList}
                            value={selectedUserId}
                            onChange={({ value }) => setSelectedUserId(value)}
                            propName="fullNameOrEmail"
                            excludeItems={selectedUserIds}
                            placeholder="Выберите администратора"
                        />
                    </div>
                    <button
                        type="button"
                        className={cn("btn btn-sm btn-brand ml-2", { disabled: !hasSelectedUser || !!savingKey })}
                        disabled={!hasSelectedUser || !!savingKey}
                        onClick={addUser}
                    >
                        <i className="icon-uEA01-add small pr-1" />
                        Добавить
                    </button>
                </div>
            )}
            <div className="table-responsive">
                <table className="table table-striped table-sm table--vertical-centered">
                    <thead>
                        <tr>
                            <th>Пользователь</th>
                            {billingNotificationTypes.map((type) => (
                                <th key={type.value}>
                                    {type.label}
                                </th>
                            ))}
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {users.length ? (
                            users.map(renderUser)
                        ) : (
                            <tr>
                                <td className="text-center text-muted" colSpan={billingNotificationTypes.length + 2}>
                                    Получатели не добавлены
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
}
